import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { OvertimeRequestListItem } from "@/modules/hr/overtime/domain/types";
import { formatDateWithWeekdayLao } from "@/shared/lib/date-time";
import { downloadCsvUtf8, escapeCsvCell } from "@/shared/lib/exportCsvUtf8";
import {
  Button,
  Card,
  CardContent,
  Input,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Download, HelpCircle, PlusIcon, SearchIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { overtimeApi } from "../api/client";
import {
  useOvertimeRequestSummaryQuery,
  useOvertimeRequestsQuery,
} from "../api/queries";
import {
  OTRequestListTour,
  useOTRequestListTour,
} from "../tour/OTRequestListTour";
import { OTRequestListTable } from "../ui/OTRequestListTable";
import { OTRequestSummaryCards } from "../ui/OTRequestSummaryCards";

/** Server allows max limit 500; export uses this cap. */
const MAX_EXPORT_RECORDS = 500;
const STATUS_LABEL: Record<OvertimeRequestListItem["status"], string> = {
  pending: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

function requestsToCsvRows(rows: OvertimeRequestListItem[]): string[] {
  const header = ["ວັນທີ", "ເວລາເລີ່ມ", "ເວລາສິ້ນສຸດ", "ຊົ່ວໂມງ", "ເຫດຜົນ", "ສະຖານະ"]
    .map(escapeCsvCell)
    .join(",");
  const dataRows = rows.map((row) =>
    [
      formatDateWithWeekdayLao(row.workDate),
      row.startTime,
      row.endTime,
      row.hours.toFixed(1),
      row.reason ?? "",
      STATUS_LABEL[row.status],
    ]
      .map(escapeCsvCell)
      .join(","),
  );
  return [header, ...dataRows];
}

export type OTRequestListSearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  status?: string;
  search?: string;
  /** เมื่อมีสิทธิ์ approve/list: true = เฉพาะของตัวเอง, ไม่ส่งหรือ false = ทุกคน */
  mine?: boolean;
};

export function OTRequestListPage() {
  const nav = useNavigate({ from: "/app/hr/overtime/requests" });
  const { has } = usePermissions();
  const canSeeAll =
    has(Permissions.hr_overtime.approve) || has(Permissions.hr_overtime.list);
  const search = useSearch({
    from: "/app/hr/overtime/requests",
  }) as OTRequestListSearchParams & {
    offset?: number;
    limit?: number;
  };

  const sortBy = search.sort?.[0]?.field ?? "workDate";
  const sortOrder = (search.sort?.[0]?.dir ?? "desc") as "asc" | "desc";

  const query = useMemo(
    () => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      sort: search.sort,
      mine: canSeeAll ? (search.mine ?? false) : true,
      status: search.status as "pending" | "approved" | "rejected" | undefined,
      search: search.search,
    }),
    [
      canSeeAll,
      search.offset,
      search.limit,
      search.sort,
      search.status,
      search.search,
      search.mine,
    ],
  );

  const listQuery = useOvertimeRequestsQuery(query);
  const summaryQuery = useOvertimeRequestSummaryQuery();

  const data = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;
  const limit = meta?.limit ?? 20;
  const offset = search.offset ?? 0;

  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setSearchInput(search.search ?? "");
  }, [search.search]);

  const doNav = useCallback(
    (opts: { searchVal?: string }) => {
      const s = opts.searchVal ?? searchInput;
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: search.sort,
          status: search.status,
          search: s.trim() || undefined,
          mine: search.mine,
        },
      });
    },
    [nav, search.limit, search.sort, search.status, search.mine, searchInput],
  );

  const debouncedSearch = useDebounceCallback(
    (val: string) => doNav({ searchVal: val }),
    400,
  );

  const exportQuery = useMemo(
    () => ({
      offset: 0,
      limit: MAX_EXPORT_RECORDS,
      mine: canSeeAll ? (search.mine ?? false) : true,
      status: search.status as "pending" | "approved" | "rejected" | undefined,
      search: search.search,
    }),
    [canSeeAll, search.mine, search.status, search.search],
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const result = await overtimeApi.listRequests(exportQuery);
      const rows = result?.data ?? [];
      const csv = requestsToCsvRows(rows).join("\r\n");
      const date = new Date().toISOString().slice(0, 10);
      downloadCsvUtf8(csv, `ot-requests-${date}.csv`);
      toast.success("ສົ່ງອອກສຳເລັດ");
    } catch {
      // Fetcher already shows error toast; we only reset exporting state
    } finally {
      setExporting(false);
    }
  }, [exportQuery]);

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        status: search.status,
        search: search.search,
        mine: search.mine,
      },
    });
  };

  const handleViewDetails = (row: { id: string }) => {
    nav({
      to: "/app/hr/overtime/requests/$id",
      params: { id: row.id },
      search: {
        sort: undefined,
        from: undefined,
        offset: undefined,
        limit: undefined,
        periodYear: undefined,
        periodMonth: undefined,
        status: undefined,
        search: undefined,
        employeeId: undefined,
      },
    });
  };

  const handleRequestOT = () => {
    nav({ to: "/app/hr/overtime/requests/new" });
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
          status: search.status,
          search: search.search,
          mine: search.mine,
        },
      });
    },
    [nav, search.limit, search.status, search.search, search.mine],
  );

  const tour = useOTRequestListTour();

  return (
    <>
      <Header />
      <Main>
        <OTRequestListTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="mb-6" data-tourid="ot-request-list-toolbar">
          <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-semibold text-2xl">ຄຳຂໍ OT</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                {canSeeAll
                  ? "ເບິ່ງແລະ ຈັດການຄຳຂໍ OT (ທຸກຄົນ ຫຼື ເລືອກແຟວເຕີດ້ານລຸ່ມ)"
                  : "ເບິ່ງແລະ ຈັດການຄຳຂໍ OT ຂອງທ່ານ."}
              </p>
            </div>
            <Button onClick={handleRequestOT}>
              <PlusIcon className="h-4 w-4" />
              ສ້າງຄຳຂໍ OT
            </Button>
          </div>
        </div>

        <div className="mb-6" data-tourid="ot-request-list-summary">
          <OTRequestSummaryCards
            data={summaryQuery.data}
            isLoading={summaryQuery.isLoading}
          />
        </div>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="ot-request-list-filter"
        >
          <div className="mb-4 flex flex-col gap-2 px-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[200px] flex-1">
                <SearchIcon className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="ຄົ້ນຫາຄຳຂໍ OT..."
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    debouncedSearch(e.target.value);
                  }}
                  onKeyDown={(e) =>
                    e.key === "Enter" && doNav({ searchVal: searchInput })
                  }
                  className="pl-8"
                />
              </div>
              {canSeeAll && (
                <Button
                  size="sm"
                  variant={query.mine ? "secondary" : "outline"}
                  onClick={() =>
                    nav({
                      search: {
                        offset: 0,
                        limit: search.limit ?? 20,
                        sort: search.sort,
                        status: search.status,
                        search: search.search,
                        mine: !query.mine,
                      },
                    })
                  }
                >
                  {query.mine ? "ເບິ່ງທຸກຄົນ" : "ເບິ່ງແຕ່ຂອງຂ້ອຍ"}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={exporting}
                onClick={handleExport}
              >
                <Download className="h-4 w-4" />
                {exporting ? "ກຳລັງສ້າງ..." : "ສົ່ງອອກ"}
              </Button>
            </div>
          </div>
          <div data-tourid="ot-request-list-table">
            <OTRequestListTable
              data={data}
              isLoading={listQuery.isLoading}
              offset={offset}
              limit={limit}
              totalCount={totalCount}
              onPaginationChange={handlePaginationChange}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortingChange={handleSortingChange}
              onViewDetails={handleViewDetails}
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardContent className="flex gap-3 p-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <span className="font-medium text-sm">i</span>
              </div>
              <div>
                <h3 className="font-medium text-sm">ກົດການສົ່ງຄຳຂໍ</h3>
                <p className="text-muted-foreground text-sm">
                  ຄຳຂໍ OT ຕ້ອງສົ່ງກ່ອນເວລາເລີ່ມວຽກຢ່າງໜ້ອຍ 24 ຊົ່ວໂມງ ເພື່ອໃຫ້ຜູ້ບໍລິຫານກວດສອບ.
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex gap-3 p-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <span className="font-medium text-sm">?</span>
              </div>
              <div>
                <h3 className="font-medium text-sm">ຕ້ອງການຄວາມຊ່ວຍເຫລືອ?</h3>
                <p className="text-muted-foreground text-sm">
                  ຕິດຕໍ່ຜູ້ຈັດການທີ່ມີຄວາມຮູ້ຄວາມສໍາລັບການຄິດເງິນເດືອນຂອງທ່ານ.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Main>
    </>
  );
}
